import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { dashboardTokens } from '../Dashboard/Components/dashboardCardStyles';
import { useAuth } from '../../store/AuthContext';
import { useAdminPermission } from '../../hooks/useAdminPermission';
import ThemedDateField from '../../components/atoms/ThemedDateField';
import { GETServices } from '../../services/service';
import {
  getBookingAppliances,
  getBookingAttributeOptions,
  getBookingComponents,
  getBookingJobTypes,
  SbAppliance,
  SbAttributeOption,
  SbComponent,
  SbJobType,
} from '../../services/serviceBooking';
import { GETUsers } from '../../services/users';
import { IService, IUser } from '../../utils/entities/interface';
import { buildBookingDetailState } from '../../utils/bookingNavigation';
import { findMirroredService, getPrimaryJobTypes, OTHER_SERVICE_VALUE } from '../../utils/dispatchableServices';
import { leadStatusMeta, pretty } from './PartnerLeads';
import {
  ConvertPartnerLeadPayload,
  ConvertPartnerLeadResult,
  PartialPartnerLeadAddress,
  PartnerConversionErrorData,
  PartnerLead,
  PartnerLeadConversionError,
  PartnerLeadConversionPreview,
  convertPartnerLead,
  getPartnerLeadConversionPreview,
  updatePartnerLead,
} from '../../services/channelPartners';

type ViewState = 'details' | 'form' | 'confirm' | 'result';

const SLOT_OPTIONS: { value: 'FIRST_HALF' | 'SECOND_HALF'; label: string }[] = [
  { value: 'FIRST_HALF', label: 'First half' },
  { value: 'SECOND_HALF', label: 'Second half' },
];

const PANEL_WIDTH = 440;

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || '?';

const formatAddress = (lead: PartnerLead) =>
  [lead.address?.line1, lead.address?.line2, lead.address?.town, lead.address?.county, lead.address?.postcode]
    .filter(Boolean)
    .join(', ');

const inputSx = {
  '.MuiOutlinedInput-root': {
    bgcolor: '#1c1c1c',
    color: '#fff',
    fontSize: 13.5,
  },
  '.MuiOutlinedInput-notchedOutline': { borderColor: dashboardTokens.border },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#444' },
};

const dateFieldWrapperSx = {
  '.MuiOutlinedInput-root': { bgcolor: '#1c1c1c', color: '#fff', fontSize: 13.5 },
  '.MuiOutlinedInput-notchedOutline': { borderColor: dashboardTokens.border },
};

const sectionLabelSx = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0.6,
  color: dashboardTokens.textSecondary,
  textTransform: 'uppercase' as const,
};

const toggleSx = {
  color: '#94a3b8',
  borderColor: dashboardTokens.border,
  fontSize: 12.5,
  textTransform: 'none' as const,
  '&.Mui-selected': {
    bgcolor: '#7B1FA2',
    color: '#fff',
    '&:hover': { bgcolor: '#5E167A' },
  },
};

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box
    sx={{
      display: 'flex',
      gap: 2,
      py: 1.25,
      borderBottom: `1px solid ${dashboardTokens.border}`,
      alignItems: 'flex-start',
    }}
  >
    <Typography sx={{ fontSize: 12.5, color: dashboardTokens.textSecondary, width: 110, flexShrink: 0 }}>
      {label}
    </Typography>
    <Box sx={{ fontSize: 13.5, color: '#e2e8f0', minWidth: 0, wordBreak: 'break-word', whiteSpace: 'pre-line' }}>
      {children}
    </Box>
  </Box>
);

const humanise = (name: string) =>
  (name || '').replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

interface ConvertPartnerLeadPanelProps {
  lead: PartnerLead | null;
  onClose: () => void;
  /** Called after a successful (or already-happened) conversion so the caller can refetch its list. */
  onConverted?: () => void;
}

export const ConvertPartnerLeadPanel: React.FC<ConvertPartnerLeadPanelProps> = ({ lead, onClose, onConverted }) => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const { canEdit } = useAdminPermission();
  // partnerLeads' POST .../convert is gated the same way the status-update
  // PATCH is (see partnerLead.service.js#updateStatus / adminRoles.js —
  // canPerformAction falls back to the 'edit' list for a 'create' action), so
  // this mirrors the edit-gated buttons already on PartnerLeadDetail.tsx
  // rather than a separate canCreate check.
  const canConvert = canEdit('partnerLeads');

  const [view, setView] = useState<ViewState>('details');
  const [preview, setPreview] = useState<PartnerLeadConversionPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const [services, setServices] = useState<IService[]>([]);
  const [jobTypes, setJobTypes] = useState<SbJobType[]>([]);

  // Appliance -> Component -> "Other" intake catalog — mirrors
  // ConvertLeadPanel.tsx's own copy of this.
  const [appliances, setAppliances] = useState<SbAppliance[]>([]);
  const [applianceKey, setApplianceKey] = useState('');
  const [applianceLabel, setApplianceLabel] = useState('');
  const [components, setComponents] = useState<SbComponent[]>([]);
  const [componentKey, setComponentKey] = useState('');
  const [componentLabel, setComponentLabel] = useState('');
  const [applianceOptions, setApplianceOptions] = useState<SbAttributeOption[]>([]);
  const [problemOptions, setProblemOptions] = useState<SbAttributeOption[]>([]);
  const [applianceOrPartType, setApplianceOrPartType] = useState('');
  const [problemDescription, setProblemDescription] = useState('');

  // New-vs-existing customer override — mirrors ConvertLeadPanel.tsx.
  const [existingUserOverride, setExistingUserOverride] = useState<{
    id: string;
    name: string;
    phoneNumber: string;
    createdAt?: string;
    address?: PartialPartnerLeadAddress | null;
  } | null>(null);
  const [customerSearchOpen, setCustomerSearchOpen] = useState(false);
  const [customerSearchOptions, setCustomerSearchOptions] = useState<IUser[]>([]);
  const [customerSearching, setCustomerSearching] = useState(false);
  const customerSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // The Select's own value — an SbJobType._id (or key), or OTHER_SERVICE_VALUE.
  // subType and serviceId are *derived* from it in handleServiceChange.
  const [selectedKey, setSelectedKey] = useState('');
  const [serviceId, setServiceId] = useState('');
  const [subType, setSubType] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [propertyType, setPropertyType] = useState<'RESIDENTIAL' | 'COMMERCIAL' | ''>('');
  const [line1, setLine1] = useState('');
  const [line2, setLine2] = useState('');
  const [town, setTown] = useState('');
  const [county, setCounty] = useState('');
  const [postcode, setPostcode] = useState('');
  const [slot, setSlot] = useState<'FIRST_HALF' | 'SECOND_HALF' | ''>('');
  const [date, setDate] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [confirmData, setConfirmData] = useState<PartnerConversionErrorData | null>(null);
  const [freshResult, setFreshResult] = useState<ConvertPartnerLeadResult | null>(null);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedJustNow, setSavedJustNow] = useState(false);

  const resetForm = useCallback((current: PartnerLead) => {
    const draft = current.conversionDraft;
    setSelectedKey('');
    setServiceId('');
    setSubType(draft?.subType || '');
    setQuantity(draft?.quantity || current.quantity || 1);
    setPropertyType(draft?.propertyType || '');
    setLine1(current.address?.line1 || '');
    setLine2(current.address?.line2 || '');
    setTown(current.address?.town || '');
    setCounty(current.address?.county || '');
    setPostcode(current.address?.postcode || '');
    setSlot('');
    setDate('');
    setSubmitError(null);
    setConfirmData(null);
    setFreshResult(null);
    setSaveError(null);
    setSavedJustNow(false);
    setAppliances([]);
    setApplianceKey(draft?.applianceKey || '');
    setApplianceLabel(draft?.applianceLabel || '');
    setComponents([]);
    setComponentKey(draft?.componentKey || '');
    setComponentLabel(draft?.componentLabel || '');
    setApplianceOrPartType(draft?.customAttributes?.applianceOrPartType || '');
    setProblemDescription(draft?.customAttributes?.problemDescription || '');
    setExistingUserOverride(null);
    setCustomerSearchOpen(false);
    setCustomerSearchOptions([]);
  }, []);

  const loadPreview = useCallback(async (leadId: string) => {
    try {
      setPreviewLoading(true);
      setPreviewError(null);
      const result = await getPartnerLeadConversionPreview(leadId);
      setPreview(result);
      setView(result.alreadyConverted ? 'result' : 'details');
    } catch (error) {
      setPreviewError(error instanceof Error ? error.message : 'Failed to load conversion details');
      setView('details');
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!lead) return;
    resetForm(lead);
    setPreview(null);
    loadPreview(lead._id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?._id]);

  useEffect(() => {
    // Fetched purely to resolve a mirrored legacy Service._id (if one
    // exists) for whichever catalog job type gets picked below — see
    // utils/dispatchableServices.ts.
    GETServices({ page: 1, limit: 100 })
      .then((response) => setServices(response.data || []))
      .catch(() => setServices([]));
    getBookingJobTypes('primary')
      .then((result) => setJobTypes(result || []))
      .catch(() => setJobTypes([]));
  }, []);

  const primaryJobTypes = getPrimaryJobTypes(jobTypes);

  // Resolve an existing subType (from a saved draft — see resetForm — or the
  // partner-reported guess below) back to the catalog job type it came from,
  // once jobTypes/services have loaded. Mirrors ConvertLeadPanel.tsx.
  useEffect(() => {
    if (!lead || selectedKey || !subType) return;
    if (subType === 'OTHER') {
      setSelectedKey(OTHER_SERVICE_VALUE);
      return;
    }
    const jobType = jobTypes.find((jt) => jt.key.toUpperCase() === subType);
    if (jobType) {
      setSelectedKey(jobType._id || jobType.key);
      setServiceId(lead.conversionDraft?.serviceId || findMirroredService(services, jobType.key)?._id || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?._id, jobTypes, services, subType]);

  // The partner already told us productType/jobType — when it's an AC job and
  // there's no saved draft to resume, pre-select the catalog job type whose
  // key matches so the admin doesn't have to re-pick something the partner
  // already specified. Purely a UX default: they can still change it, and any
  // other productType (heat_pump/boiler/other — nothing the catalog or
  // Enquiry.subType models today) is left unselected.
  useEffect(() => {
    if (!lead || subType) return;
    const partnerJobType = lead.jobType;
    if (lead.productType !== 'air_conditioning' || !partnerJobType) return;
    // SbJobType.key is already lowercase, matching PartnerLead.jobType
    // ('service' | 'repair' | 'installation') directly — no case conversion.
    const match = jobTypes.find((jt) => jt.key === partnerJobType);
    if (match) setSubType(match.key.toUpperCase());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?._id, jobTypes, subType]);

  // Changing the job type invalidates whatever appliance/component/intake
  // answers were picked under the previous one.
  const resetCatalogSteps = useCallback(() => {
    setApplianceKey('');
    setApplianceLabel('');
    setComponents([]);
    setComponentKey('');
    setComponentLabel('');
    setApplianceOrPartType('');
    setProblemDescription('');
  }, []);

  const handleServiceChange = useCallback(
    (id: string) => {
      setSelectedKey(id);
      resetCatalogSteps();
      if (id === OTHER_SERVICE_VALUE) {
        setSubType('OTHER');
        setServiceId('');
        return;
      }
      const jobType = jobTypes.find((jt) => jt._id === id || jt.key === id);
      if (!jobType) {
        setSubType('');
        setServiceId('');
        return;
      }
      setSubType(jobType.key.toUpperCase());
      setServiceId(findMirroredService(services, jobType.key)?._id || '');
    },
    [jobTypes, services, resetCatalogSteps],
  );

  const handleApplianceChange = useCallback(
    (key: string) => {
      setApplianceKey(key);
      setApplianceLabel(appliances.find((a) => a.key === key)?.label || '');
      setComponents([]);
      setComponentKey('');
      setComponentLabel('');
      setApplianceOrPartType('');
      setProblemDescription('');
    },
    [appliances],
  );

  const handleComponentChange = useCallback(
    (key: string) => {
      setComponentKey(key);
      setComponentLabel(components.find((c) => c.key === key)?.label || '');
      setApplianceOrPartType('');
      setProblemDescription('');
    },
    [components],
  );

  const activeJobType = jobTypes.find((jt) => (jt._id || jt.key) === selectedKey);
  const isOtherJobType = selectedKey === OTHER_SERVICE_VALUE;
  const matchedComponent = components.find((c) => c.key === componentKey);
  const showIntake = isOtherJobType || Boolean(matchedComponent?.triggersIntakeForm);

  useEffect(() => {
    if (!activeJobType) {
      setAppliances([]);
      return;
    }
    getBookingAppliances(activeJobType.key)
      .then((result) => setAppliances(result || []))
      .catch(() => setAppliances([]));
  }, [activeJobType?.key]);

  useEffect(() => {
    if (!applianceKey) {
      setComponents([]);
      return;
    }
    getBookingComponents(applianceKey)
      .then((result) => setComponents(result || []))
      .catch(() => setComponents([]));
  }, [applianceKey]);

  useEffect(() => {
    getBookingAttributeOptions('ac_type')
      .then((result) => setApplianceOptions(result || []))
      .catch(() => setApplianceOptions([]));
    getBookingAttributeOptions('problem_list')
      .then((result) => setProblemOptions(result || []))
      .catch(() => setProblemOptions([]));
  }, []);

  const searchCustomers = useCallback((query: string) => {
    if (customerSearchTimer.current) clearTimeout(customerSearchTimer.current);
    if (!query.trim()) {
      setCustomerSearchOptions([]);
      return;
    }
    customerSearchTimer.current = setTimeout(async () => {
      try {
        setCustomerSearching(true);
        const result = await GETUsers({ searchQuery: query.trim(), limit: 8 });
        setCustomerSearchOptions(result.data || []);
      } catch {
        setCustomerSearchOptions([]);
      } finally {
        setCustomerSearching(false);
      }
    }, 300);
  }, []);

  const effectiveCustomer = existingUserOverride || preview?.existingUser || null;

  const applySavedAddress = useCallback((address: PartialPartnerLeadAddress) => {
    setLine1(address.line1 || '');
    setLine2(address.line2 || '');
    setTown(address.town || '');
    setCounty(address.county || '');
    setPostcode(address.postcode || '');
  }, []);

  const buildPayload = useCallback(
    (force: boolean): ConvertPartnerLeadPayload => ({
      subType,
      serviceId: serviceId || undefined,
      quantity: (applianceKey && componentKey) || isOtherJobType ? quantity : undefined,
      propertyType: propertyType || undefined,
      applianceKey: applianceKey || undefined,
      applianceLabel: applianceLabel || undefined,
      componentKey: componentKey || undefined,
      componentLabel: componentLabel || undefined,
      customAttributes:
        applianceOrPartType.trim() || problemDescription.trim()
          ? { applianceOrPartType: applianceOrPartType.trim(), problemDescription: problemDescription.trim() }
          : undefined,
      existingUserId: existingUserOverride?.id || undefined,
      address: {
        line1: line1.trim(),
        line2: line2.trim(),
        town: town.trim(),
        county: county.trim(),
        postcode: postcode.trim(),
      },
      slot: slot || undefined,
      date: date || undefined,
      force,
    }),
    [
      subType,
      serviceId,
      selectedKey,
      isOtherJobType,
      quantity,
      propertyType,
      applianceKey,
      applianceLabel,
      componentKey,
      componentLabel,
      applianceOrPartType,
      problemDescription,
      existingUserOverride,
      line1,
      line2,
      town,
      county,
      postcode,
      slot,
      date,
    ],
  );

  // Convert produces a scheduled job, so subType + slot + date are all
  // mandatory; a real (non-OTHER) job type also needs an appliance +
  // component picked, and the Step-4 intake form (when shown) needs a
  // problem description.
  const catalogStepsComplete = isOtherJobType || Boolean(applianceKey && componentKey);
  const canSubmit = Boolean(
    subType && slot && date && catalogStepsComplete && (!showIntake || problemDescription.trim()),
  );

  const handleSave = useCallback(async () => {
    if (!lead) return;
    try {
      setSaving(true);
      setSaveError(null);
      setSavedJustNow(false);
      const conversionDraft: NonNullable<Parameters<typeof updatePartnerLead>[1]['conversionDraft']> = {};
      if (subType) conversionDraft.subType = subType;
      if (serviceId) conversionDraft.serviceId = serviceId;
      if (quantity) conversionDraft.quantity = quantity;
      if (propertyType) conversionDraft.propertyType = propertyType;
      if (applianceKey) conversionDraft.applianceKey = applianceKey;
      if (applianceLabel) conversionDraft.applianceLabel = applianceLabel;
      if (componentKey) conversionDraft.componentKey = componentKey;
      if (componentLabel) conversionDraft.componentLabel = componentLabel;
      if (applianceOrPartType.trim() || problemDescription.trim()) {
        conversionDraft.customAttributes = {
          applianceOrPartType: applianceOrPartType.trim(),
          problemDescription: problemDescription.trim(),
        };
      }

      await updatePartnerLead(lead._id, {
        address: {
          line1: line1.trim(),
          line2: line2.trim(),
          town: town.trim(),
          county: county.trim(),
          postcode: postcode.trim(),
        },
        ...(Object.keys(conversionDraft).length ? { conversionDraft } : {}),
      });
      setSavedJustNow(true);
      // Reuses the list-refetch callback the panel already exposes for
      // conversion — a save changes the same underlying lead record, so the
      // parent list (and a later reopen of this panel) should see it too.
      onConverted?.();
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  }, [
    lead,
    subType,
    serviceId,
    quantity,
    propertyType,
    applianceKey,
    applianceLabel,
    componentKey,
    componentLabel,
    applianceOrPartType,
    problemDescription,
    line1,
    line2,
    town,
    county,
    postcode,
    onConverted,
  ]);

  // "Saved" only reflects the state at the moment Save was clicked — clear it
  // as soon as anything it covers changes again.
  useEffect(() => {
    setSavedJustNow(false);
  }, [
    line1,
    line2,
    town,
    county,
    postcode,
    subType,
    serviceId,
    quantity,
    propertyType,
    applianceKey,
    componentKey,
    applianceOrPartType,
    problemDescription,
  ]);

  const handleSubmit = useCallback(
    async (force: boolean) => {
      if (!lead || !canSubmit) return;
      try {
        setSubmitting(true);
        setSubmitError(null);
        const result = await convertPartnerLead(lead._id, buildPayload(force));
        setFreshResult(result);
        setConfirmData(null);
        setView('result');
        onConverted?.();
      } catch (error) {
        if (error instanceof PartnerLeadConversionError) {
          if (error.statusCode === 409 && error.data?.requiresConfirmation) {
            setConfirmData(error.data);
            setView('confirm');
            return;
          }
          if (error.statusCode === 409) {
            // Already converted — either this very click lost a race, or the
            // lead was converted earlier. Pull the authoritative record.
            await loadPreview(lead._id);
            onConverted?.();
            return;
          }
          setSubmitError(error.message);
          return;
        }
        setSubmitError('Something went wrong. Please try again.');
      } finally {
        setSubmitting(false);
      }
    },
    [lead, canSubmit, buildPayload, onConverted, loadPreview],
  );

  if (!lead) return null;

  const statusKey = view === 'result' ? 'CONVERTED' : lead.status;
  const notConvertible = Boolean(preview && !preview.convertible && !preview.alreadyConverted);

  const convertBlockedReason = previewLoading
    ? null
    : previewError
    ? previewError
    : !canConvert
    ? "You don't have permission to convert leads."
    : notConvertible
    ? `This lead is ${leadStatusMeta[statusKey].label} — move it to Contacted before converting.`
    : null;

  const header = (
    <Box sx={{ px: 2.5, pt: 2.5, pb: 1.5, flexShrink: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#fff' }}>
          {lead.customerName || 'Unnamed customer'}
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: '#94a3b8', mt: -0.5 }} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
        <Typography sx={{ fontSize: 12.5, color: dashboardTokens.textSecondary }}>{lead.channelPartnerLeadId}</Typography>
        <Chip
          label={leadStatusMeta[statusKey].label}
          size="small"
          sx={{ bgcolor: leadStatusMeta[statusKey].bg, color: leadStatusMeta[statusKey].color, fontWeight: 700, height: 20 }}
        />
      </Box>
    </Box>
  );

  // Surfaced up front (Details AND Form) rather than only after a 409 —
  // mirrors ConvertLeadPanel.tsx's own copy.
  const renderCustomerCard = () => {
    if (!preview) return null;
    const auto = preview.existingUser;
    const active = existingUserOverride || auto;

    return (
      <Box sx={{ mb: 2.5 }}>
        <Typography sx={sectionLabelSx}>Customer</Typography>
        {active ? (
          <Box sx={{ mt: 1, p: 1.25, borderRadius: 2, border: `1px solid ${dashboardTokens.border}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    bgcolor: '#7B1FA2',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12.5,
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {initials(active.name || '?')}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, color: '#fff', fontWeight: 600 }}>
                    {active.name || 'Unnamed customer'}
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, color: dashboardTokens.textSecondary }}>
                    {active.phoneNumber}
                    {active.createdAt ? ` · customer since ${dayjs(active.createdAt).format('MMM YYYY')}` : ''}
                  </Typography>
                </Box>
              </Box>
              {existingUserOverride && (
                <Chip label="manually attached" size="small" sx={{ height: 18, fontSize: 10, bgcolor: '#1c1c1c', color: '#94a3b8' }} />
              )}
            </Box>
          </Box>
        ) : (
          <Box sx={{ mt: 1, p: 1.25, borderRadius: 2, border: `1px dashed ${dashboardTokens.border}` }}>
            <Typography sx={{ fontSize: 12.5, color: dashboardTokens.textSecondary }}>
              No match for {lead.customerPhoneNumber} — a new customer account will be created.
            </Typography>
          </Box>
        )}

        {existingUserOverride ? (
          <Typography
            onClick={() => setExistingUserOverride(null)}
            sx={{ fontSize: 11.5, color: '#a78bfa', cursor: 'pointer', mt: 0.75 }}
          >
            {auto ? 'Use the auto-matched customer instead' : 'Clear — create a new customer instead'}
          </Typography>
        ) : (
          <Typography
            onClick={() => setCustomerSearchOpen((v) => !v)}
            sx={{ fontSize: 11.5, color: '#a78bfa', cursor: 'pointer', mt: 0.75 }}
          >
            {customerSearchOpen
              ? 'Cancel search'
              : auto
              ? 'Not them? Attach a different customer'
              : 'Attach to an existing customer instead'}
          </Typography>
        )}

        {customerSearchOpen && !existingUserOverride && (
          <Autocomplete
            size="small"
            options={customerSearchOptions}
            loading={customerSearching}
            getOptionLabel={(option) => `${option.name || 'Unnamed'} · ${option.phoneNumber}`}
            filterOptions={(options) => options}
            onInputChange={(_, value) => searchCustomers(value)}
            onChange={(_, value) => {
              if (!value) return;
              setExistingUserOverride({ id: value._id, name: value.name, phoneNumber: value.phoneNumber });
              setCustomerSearchOpen(false);
            }}
            renderInput={(params) => (
              <TextField {...params} placeholder="Search by name or phone" sx={{ ...inputSx, mt: 1 }} />
            )}
            noOptionsText={customerSearching ? 'Searching…' : 'No customers found'}
          />
        )}
      </Box>
    );
  };

  const renderDetails = () => (
    <Box sx={{ px: 2.5, pb: 2 }}>
      {renderCustomerCard()}

      <Box sx={{ borderLeft: '3px solid #7B1FA2', pl: 1.25, mb: 1 }}>
        <Typography sx={sectionLabelSx}>Reported by partner</Typography>
        <Typography sx={{ fontSize: 11.5, color: dashboardTokens.textMuted, mt: 0.25 }}>
          Read-only — this is what the channel partner submitted.
        </Typography>
      </Box>

      <Row label="Phone">{lead.customerPhoneNumber}</Row>
      <Row label="Address">{formatAddress(lead)}</Row>
      <Row label="Product">{pretty(lead.productType)}</Row>
      {lead.jobType && <Row label="Job type">{pretty(lead.jobType)}</Row>}
      {lead.equipmentType && <Row label="Equipment">{pretty(lead.equipmentType)}</Row>}
      <Row label="Quantity">{lead.quantity}</Row>
      <Row label="Partner">
        {lead.channelPartner?.fullName || lead.channelPartner?.phoneNumber || '—'}
      </Row>
      {lead.notes && <Row label="Notes">{lead.notes}</Row>}
      {!!lead.photos?.length && <Row label="Photos">{lead.photos.length} attached</Row>}
    </Box>
  );

  const renderForm = () => (
    <Box sx={{ px: 2.5, pb: 2 }}>
      <Box
        sx={{
          borderLeft: '3px solid #7B1FA2',
          pl: 1.25,
          py: 0.75,
          mb: 2,
          bgcolor: '#1a1520',
          borderRadius: '0 8px 8px 0',
        }}
      >
        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, color: dashboardTokens.textSecondary }}>
          REPORTED BY PARTNER
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography sx={{ fontSize: 12.5, color: dashboardTokens.textSecondary }}>Phone</Typography>
            <Typography sx={{ fontSize: 12.5, color: '#fff', fontWeight: 600 }}>{lead.customerPhoneNumber}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography sx={{ fontSize: 12.5, color: dashboardTokens.textSecondary }}>Product</Typography>
            <Typography sx={{ fontSize: 12.5, color: '#fff', fontWeight: 600 }}>
              {pretty(lead.productType)}{lead.jobType ? ` · ${pretty(lead.jobType)}` : ''}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography sx={{ fontSize: 12.5, color: dashboardTokens.textSecondary }}>Address</Typography>
            <Typography sx={{ fontSize: 12.5, color: '#fff', fontWeight: 600, textAlign: 'right', maxWidth: 220 }}>
              {formatAddress(lead)}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>Convert to enquiry</Typography>
      <Typography sx={{ fontSize: 12, color: dashboardTokens.textMuted, mb: 2 }}>
        Confirm the address and which catalog service this job maps to — the phone is already verified.
      </Typography>

      {submitError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {submitError}
        </Alert>
      )}

      {renderCustomerCard()}

      {/* Address first, then category — mirrors ConvertLeadPanel.tsx. The
          partner's submission already required a complete address, but ops
          can still correct it here while confirming details with the
          customer. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: 12, color: '#e2e8f0' }}>Address</Typography>
        {effectiveCustomer?.address && (effectiveCustomer.address.line1 || effectiveCustomer.address.postcode) && (
          <Typography
            onClick={() => applySavedAddress(effectiveCustomer.address!)}
            sx={{ fontSize: 11, color: '#a78bfa', cursor: 'pointer', ml: 'auto' }}
          >
            Use {effectiveCustomer.name || 'their'} saved address
          </Typography>
        )}
      </Box>
      <TextField
        fullWidth
        size="small"
        placeholder="Address line 1"
        value={line1}
        onChange={(event) => setLine1(event.target.value)}
        sx={{ ...inputSx, mb: 1.25 }}
      />
      <TextField
        fullWidth
        size="small"
        placeholder="Address line 2 (optional)"
        value={line2}
        onChange={(event) => setLine2(event.target.value)}
        sx={{ ...inputSx, mb: 1.25 }}
      />
      <Box sx={{ display: 'flex', gap: 1.5, mb: 1.25 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Town"
          value={town}
          onChange={(event) => setTown(event.target.value)}
          sx={inputSx}
        />
        <TextField
          fullWidth
          size="small"
          placeholder="County"
          value={county}
          onChange={(event) => setCounty(event.target.value)}
          sx={inputSx}
        />
      </Box>
      <TextField
        fullWidth
        size="small"
        placeholder="Postcode"
        value={postcode}
        onChange={(event) => setPostcode(event.target.value.toUpperCase())}
        sx={{ ...inputSx, mb: 2 }}
      />

      <Typography sx={{ fontSize: 12, color: '#e2e8f0', mb: 0.5 }}>Enquiry type *</Typography>
      <Select
        fullWidth
        size="small"
        displayEmpty
        value={selectedKey}
        onChange={(event) => handleServiceChange(event.target.value)}
        sx={{ ...inputSx, mb: selectedKey && selectedKey !== OTHER_SERVICE_VALUE ? 1.25 : 2, '.MuiSelect-select': { color: selectedKey ? '#fff' : '#64748b' } }}
      >
        <MenuItem value="" disabled>
          Select type
        </MenuItem>
        {primaryJobTypes.map((jobType) => (
          <MenuItem key={jobType._id || jobType.key} value={jobType._id || jobType.key}>
            {jobType.label}
          </MenuItem>
        ))}
        <MenuItem value={OTHER_SERVICE_VALUE}>Other (not in catalog)</MenuItem>
      </Select>

      {/* Step 2-3: Appliance -> Component — mirrors ConvertLeadPanel.tsx. */}
      {activeJobType && (
        <>
          <Typography sx={{ fontSize: 12, color: '#e2e8f0', mb: 0.5 }}>Appliance *</Typography>
          <Select
            fullWidth
            size="small"
            displayEmpty
            value={applianceKey}
            onChange={(event) => handleApplianceChange(event.target.value)}
            sx={{ ...inputSx, mb: 1.25, '.MuiSelect-select': { color: applianceKey ? '#fff' : '#64748b' } }}
          >
            <MenuItem value="" disabled>
              {appliances.length ? 'Select appliance' : 'Loading…'}
            </MenuItem>
            {appliances.map((appliance) => (
              <MenuItem key={appliance.key} value={appliance.key}>
                {appliance.label}
              </MenuItem>
            ))}
          </Select>

          {applianceKey && (
            <>
              <Typography sx={{ fontSize: 12, color: '#e2e8f0', mb: 0.5 }}>Component *</Typography>
              <Select
                fullWidth
                size="small"
                displayEmpty
                value={componentKey}
                onChange={(event) => handleComponentChange(event.target.value)}
                sx={{ ...inputSx, mb: 1.25, '.MuiSelect-select': { color: componentKey ? '#fff' : '#64748b' } }}
              >
                <MenuItem value="" disabled>
                  {components.length ? 'Select component' : 'Loading…'}
                </MenuItem>
                {components.map((component) => (
                  <MenuItem key={component.key} value={component.key}>
                    {component.label}
                  </MenuItem>
                ))}
              </Select>
              {!components.length && (
                <Typography sx={{ fontSize: 11, color: '#f87171', mt: -0.75, mb: 1.25 }}>
                  No components configured for this appliance — contact IT, or pick "Other (not in catalog)"
                  above instead.
                </Typography>
              )}
            </>
          )}
        </>
      )}

      {/* Step 4: intake form — mirrors ConvertLeadPanel.tsx. */}
      {showIntake && (
        <Box sx={{ p: 1.5, borderRadius: 2, border: `1px solid ${dashboardTokens.border}`, mb: 1.25 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#e2e8f0', mb: 1 }}>
            Tell us what this job is
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#e2e8f0', mb: 0.5 }}>Appliance / part type</Typography>
          <Autocomplete
            size="small"
            freeSolo
            options={applianceOptions.map((o) => o.label)}
            inputValue={applianceOrPartType}
            onInputChange={(_, value) => setApplianceOrPartType(value)}
            renderInput={(params) => (
              <TextField {...params} placeholder="e.g. Split AC indoor unit" sx={{ ...inputSx, mb: 1.25 }} />
            )}
          />
          <Typography sx={{ fontSize: 12, color: '#e2e8f0', mb: 0.5 }}>Problem *</Typography>
          <Autocomplete
            size="small"
            freeSolo
            options={problemOptions.map((o) => o.label)}
            inputValue={problemDescription}
            onInputChange={(_, value) => setProblemDescription(value)}
            renderInput={(params) => (
              <TextField {...params} placeholder="What's wrong with it?" sx={inputSx} />
            )}
          />
        </Box>
      )}

      {(applianceKey && componentKey) || isOtherJobType ? (
        <Box sx={{ width: 90, mb: 2 }}>
          <Typography sx={{ fontSize: 12, color: '#e2e8f0', mb: 0.5 }}>Qty</Typography>
          <TextField
            fullWidth
            size="small"
            type="number"
            inputProps={{ min: 1, max: 999 }}
            value={quantity}
            onChange={(event) => setQuantity(Math.max(1, Number(event.target.value) || 1))}
            sx={inputSx}
          />
        </Box>
      ) : null}

      <Typography sx={{ fontSize: 12, color: '#e2e8f0', mb: 0.5 }}>Property type</Typography>
      <ToggleButtonGroup
        exclusive
        fullWidth
        value={propertyType}
        onChange={(_, next) => setPropertyType(next || '')}
        sx={{ mb: 2 }}
      >
        <ToggleButton value="RESIDENTIAL" sx={toggleSx}>
          Residential
        </ToggleButton>
        <ToggleButton value="COMMERCIAL" sx={toggleSx}>
          Commercial
        </ToggleButton>
      </ToggleButtonGroup>

      {/* Slot + date are required: converting creates a real dispatchable job
          and the /enquires tabs filter on both. */}
      <Typography sx={{ fontSize: 12, color: '#e2e8f0', mb: 0.5 }}>Slot *</Typography>
      <ToggleButtonGroup
        exclusive
        fullWidth
        value={slot}
        onChange={(_, next) => setSlot(next || '')}
        sx={{ mb: 2 }}
      >
        {SLOT_OPTIONS.map((option) => (
          <ToggleButton key={option.value} value={option.value} sx={toggleSx}>
            {option.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <Typography sx={{ fontSize: 12, color: '#e2e8f0', mb: 0.5 }}>Date *</Typography>
      <Box sx={dateFieldWrapperSx}>
        <ThemedDateField value={date || null} onChange={setDate} fullWidth minDate={dayjs().format('YYYY-MM-DD')} />
      </Box>
      <Typography sx={{ fontSize: 11, color: '#94a3b8', mt: 0.75 }}>
        Converting schedules the job and lists it under Enquiries.
      </Typography>
    </Box>
  );

  const renderConfirm = () => (
    <Box sx={{ px: 2.5, pb: 2 }}>
      {(confirmData?.warnings || []).map((warning) => (
        <Alert key={warning.code} severity="warning" icon={<WarningAmberOutlinedIcon fontSize="inherit" />} sx={{ mb: 2 }}>
          {warning.message}
        </Alert>
      ))}

      {confirmData?.existingUser && (
        <Box sx={{ mb: 2.5 }}>
          <Typography sx={sectionLabelSx}>Existing customer</Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              mt: 1,
              p: 1.25,
              borderRadius: 2,
              border: `1px solid ${dashboardTokens.border}`,
            }}
          >
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: '50%',
                bgcolor: '#7B1FA2',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 13,
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              {initials(confirmData.existingUser.name || '?')}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 13.5, color: '#fff', fontWeight: 600 }}>
                {confirmData.existingUser.name || 'Unnamed customer'}
              </Typography>
              <Typography sx={{ fontSize: 12, color: dashboardTokens.textSecondary }}>
                {confirmData.existingUser.phoneNumber}
                {confirmData.existingUser.createdAt &&
                  ` · customer since ${dayjs(confirmData.existingUser.createdAt).format('MMM YYYY')}`}
              </Typography>
            </Box>
          </Box>
          <Typography sx={{ fontSize: 11.5, color: dashboardTokens.textMuted, mt: 0.75 }}>
            This account will be reused — no duplicate customer is created.
          </Typography>
        </Box>
      )}

      {!!confirmData?.openEnquiries?.length && (
        <Box sx={{ mb: 2.5 }}>
          <Typography sx={sectionLabelSx}>
            {confirmData.openEnquiries.length} open {confirmData.openEnquiries.length === 1 ? 'enquiry' : 'enquiries'}
          </Typography>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {confirmData.openEnquiries.map((enquiry) => (
              <Box
                key={enquiry.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 1.25,
                  borderRadius: 2,
                  border: `1px solid ${dashboardTokens.border}`,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: 13, color: '#fff', fontWeight: 600 }}>
                      {enquiry.enquiryNumber}
                    </Typography>
                    <Chip label={enquiry.status} size="small" sx={{ height: 18, fontSize: 10, bgcolor: '#22c55e22', color: '#22c55e' }} />
                  </Box>
                  <Typography sx={{ fontSize: 11.5, color: dashboardTokens.textMuted }}>
                    {humanise(enquiry.subType)}
                    {enquiry.createdAt && ` · raised ${dayjs(enquiry.createdAt).format('D MMM YYYY')}`}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {!!confirmData?.otherConvertedLeads?.length && (
        <Box sx={{ mb: 1 }}>
          <Typography sx={sectionLabelSx}>Already converted once</Typography>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {confirmData.otherConvertedLeads.map((other) => (
              <Box key={other.id} sx={{ p: 1.25, borderRadius: 2, border: `1px solid ${dashboardTokens.border}` }}>
                <Typography sx={{ fontSize: 13, color: '#fff', fontWeight: 600 }}>
                  {other.channelPartnerLeadId} · {pretty(other.productType)}
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: dashboardTokens.textMuted, mt: 0.25 }}>
                  {other.createdAt && `Submitted ${dayjs(other.createdAt).format('D MMM YYYY')} → `}{other.enquiryNumber}
                </Typography>
              </Box>
            ))}
          </Box>
          <Typography sx={{ fontSize: 11.5, color: dashboardTokens.textMuted, mt: 0.75 }}>
            Same number, different partner lead.
          </Typography>
        </Box>
      )}
    </Box>
  );

  const renderResult = () => {
    const enquiryNumber = freshResult?.enquiry.enquiryNumber || preview?.conversion?.enquiryNumber || '';
    // /enquires lists BOOKINGS, so the job is opened by booking id — not the
    // enquiry id. Leads converted before conversion created a booking have
    // none, and simply don't get the button.
    const bookingId = freshResult?.booking?.id || preview?.conversion?.booking || '';
    const convertedAt = freshResult ? new Date().toISOString() : preview?.conversion?.convertedAt;

    return (
      <Box sx={{ px: 2.5, pb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            p: 1.5,
            borderRadius: 2,
            bgcolor: '#0f2a17',
            border: '1px solid #16532b',
            mb: 2.5,
          }}
        >
          <CheckCircleOutlineIcon sx={{ color: '#4ade80', fontSize: 20, mt: 0.25 }} />
          <Box>
            <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#86efac' }}>
              Converted to {enquiryNumber || 'an enquiry'}
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: '#86efac', opacity: 0.85, mt: 0.25 }}>
              {freshResult
                ? `by ${authState.userName || 'you'} · just now`
                : convertedAt
                ? `Converted ${dayjs(convertedAt).format('D MMM YYYY, h:mm A')}`
                : 'Converted earlier'}
            </Typography>
          </Box>
        </Box>

        {freshResult ? (
          <>
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={sectionLabelSx}>Enquiry</Typography>
              <Box sx={{ mt: 1, p: 1.25, borderRadius: 2, border: `1px solid ${dashboardTokens.border}` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontSize: 13.5, color: '#fff', fontWeight: 700 }}>
                    {freshResult.enquiry.enquiryNumber}
                  </Typography>
                  <Chip label={humanise(freshResult.enquiry.status)} size="small" sx={{ height: 18, fontSize: 10, bgcolor: '#7c3aed22', color: '#a78bfa' }} />
                </Box>
                <Typography sx={{ fontSize: 12, color: dashboardTokens.textSecondary, mt: 0.25 }}>
                  {[
                    humanise(freshResult.enquiry.subType),
                    freshResult.enquiry.applianceLabel,
                    freshResult.enquiry.componentLabel,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                  {freshResult.enquiry.service &&
                    ` · ${freshResult.enquiry.service.name}${quantity > 1 ? ` × ${quantity}` : ''}`}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ mb: 2.5 }}>
              <Typography sx={sectionLabelSx}>Customer</Typography>
              <Box sx={{ mt: 1, p: 1.25, borderRadius: 2, border: `1px solid ${dashboardTokens.border}` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <Box
                      sx={{
                        width: 30,
                        height: 30,
                        borderRadius: '50%',
                        bgcolor: '#7B1FA2',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      {initials(freshResult.customer.name || '?')}
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: 13, color: '#fff', fontWeight: 600 }}>
                        {freshResult.customer.name || 'Unnamed'}
                      </Typography>
                      <Typography sx={{ fontSize: 11.5, color: dashboardTokens.textSecondary }}>
                        {freshResult.customer.phoneNumber}
                      </Typography>
                    </Box>
                  </Box>
                  {!freshResult.customer.reusedExisting && (
                    <Chip label="new account" size="small" sx={{ height: 18, fontSize: 10, bgcolor: '#1c1c1c', color: '#94a3b8' }} />
                  )}
                </Box>
                {!freshResult.customer.reusedExisting && (
                  <Typography sx={{ fontSize: 11, color: dashboardTokens.textMuted, mt: 1 }}>
                    Not yet verified — they complete OTP the first time they open the app.
                  </Typography>
                )}
              </Box>
            </Box>
          </>
        ) : (
          <Typography sx={{ fontSize: 12.5, color: dashboardTokens.textSecondary, mb: 2.5 }}>
            This lead was converted {convertedAt ? `on ${dayjs(convertedAt).format('D MMM YYYY')}` : 'earlier'}. Open
            the enquiry for full details.
          </Typography>
        )}

        <Box>
          <Typography sx={sectionLabelSx}>Reported by partner</Typography>
          <Row label="Partner">{lead.channelPartner?.fullName || lead.channelPartner?.phoneNumber || '—'}</Row>
          <Row label="Address">{formatAddress(lead)}</Row>
        </Box>

        {bookingId && (
          <Box
            onClick={() =>
              navigate(`/bookings/assign/${bookingId}`, {
                // Converting turns this into an enquiry, not a lead anymore —
                // sidebar highlight and Back should reflect that (default
                // 'enquiries' returnPath), landing on the All Enquires tab.
                state: buildBookingDetailState('enquiries', { enquiryTab: '' }),
              })
            }
            sx={{
              mt: 2.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.75,
              py: 1.1,
              borderRadius: 2,
              bgcolor: '#7B1FA2',
              color: '#fff',
              fontSize: 13.5,
              fontWeight: 700,
              cursor: 'pointer',
              '&:hover': { bgcolor: '#5E167A' },
            }}
          >
            Open enquiry <OpenInNewIcon sx={{ fontSize: 16 }} />
          </Box>
        )}
      </Box>
    );
  };

  const footer = (() => {
    if (view === 'details') {
      return (
        <Box sx={{ px: 2.5, py: 2, borderTop: `1px solid ${dashboardTokens.border}`, flexShrink: 0 }}>
          <Box
            onClick={() => {
              if (convertBlockedReason || previewLoading) return;
              setView('form');
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.75,
              py: 1.1,
              borderRadius: 2,
              bgcolor: convertBlockedReason ? '#2a2a2a' : '#7B1FA2',
              color: convertBlockedReason ? '#64748b' : '#fff',
              fontSize: 13.5,
              fontWeight: 700,
              cursor: convertBlockedReason ? 'not-allowed' : 'pointer',
              '&:hover': convertBlockedReason ? undefined : { bgcolor: '#5E167A' },
            }}
          >
            {previewLoading ? (
              <CircularProgress size={16} sx={{ color: '#94a3b8' }} />
            ) : (
              <>
                {convertBlockedReason && <LockOutlinedIcon sx={{ fontSize: 15 }} />}
                Convert to enquiry
              </>
            )}
          </Box>
          <Typography sx={{ fontSize: 11, color: convertBlockedReason ? '#f87171' : dashboardTokens.textMuted, textAlign: 'center', mt: 0.75 }}>
            {convertBlockedReason || 'Creates a customer and a fresh enquiry ops can action.'}
          </Typography>
        </Box>
      );
    }

    if (view === 'form') {
      return (
        <Box sx={{ px: 2.5, py: 2, borderTop: `1px solid ${dashboardTokens.border}`, flexShrink: 0 }}>
          {saveError && (
            <Typography sx={{ fontSize: 11, color: '#f87171', textAlign: 'center', mb: 1 }}>
              {saveError}
            </Typography>
          )}
          {!canSubmit && (
            <Typography sx={{ fontSize: 11, color: '#f87171', textAlign: 'center', mb: 1 }}>
              {!subType
                ? 'Pick an enquiry type to continue.'
                : !catalogStepsComplete
                ? 'Pick an appliance and component to continue.'
                : showIntake && !problemDescription.trim()
                ? 'Describe the problem to continue.'
                : 'Pick a slot and date — the job has to be schedulable.'}
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 1.25 }}>
            <Box
              onClick={() => setView('details')}
              sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', py: 1.1, borderRadius: 2, border: `1px solid ${dashboardTokens.border}`, color: '#e2e8f0', fontSize: 13.5, fontWeight: 600, cursor: 'pointer' }}
            >
              Cancel
            </Box>
            <Box
              onClick={() => !saving && !submitting && handleSave()}
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                py: 1.1,
                borderRadius: 2,
                border: `1px solid ${dashboardTokens.border}`,
                color: savedJustNow ? '#4ade80' : '#e2e8f0',
                fontSize: 13.5,
                fontWeight: 600,
                cursor: saving || submitting ? 'not-allowed' : 'pointer',
              }}
            >
              {saving ? <CircularProgress size={16} sx={{ color: '#94a3b8' }} /> : savedJustNow ? 'Saved' : 'Save'}
            </Box>
            <Box
              onClick={() => !submitting && canSubmit && handleSubmit(false)}
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                py: 1.1,
                borderRadius: 2,
                bgcolor: !canSubmit || submitting ? '#2a2a2a' : '#7B1FA2',
                color: !canSubmit || submitting ? '#64748b' : '#fff',
                fontSize: 13.5,
                fontWeight: 700,
                cursor: !canSubmit || submitting ? 'not-allowed' : 'pointer',
              }}
            >
              {submitting ? <CircularProgress size={16} sx={{ color: '#94a3b8' }} /> : 'Create enquiry'}
            </Box>
          </Box>
        </Box>
      );
    }

    if (view === 'confirm') {
      return (
        <Box sx={{ px: 2.5, py: 2, borderTop: `1px solid ${dashboardTokens.border}`, display: 'flex', gap: 1.25, flexShrink: 0 }}>
          <Box
            onClick={() => setView('form')}
            sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', py: 1.1, borderRadius: 2, border: `1px solid ${dashboardTokens.border}`, color: '#e2e8f0', fontSize: 13.5, fontWeight: 600, cursor: 'pointer' }}
          >
            Cancel
          </Box>
          <Box
            onClick={() => !submitting && handleSubmit(true)}
            sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', py: 1.1, borderRadius: 2, bgcolor: '#7B1FA2', color: '#fff', fontSize: 13.5, fontWeight: 700, cursor: submitting ? 'not-allowed' : 'pointer' }}
          >
            {submitting ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Convert anyway'}
          </Box>
        </Box>
      );
    }

    return null;
  })();

  return (
    <Drawer
      anchor="right"
      open={Boolean(lead)}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: PANEL_WIDTH,
          maxWidth: '100vw',
          bgcolor: dashboardTokens.cardBg,
          backgroundImage: 'none',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {header}
      <Divider sx={{ borderColor: dashboardTokens.border }} />
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {previewLoading && view === 'details' && !preview ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={22} sx={{ color: '#7B1FA2' }} />
          </Box>
        ) : (
          <>
            {view === 'details' && renderDetails()}
            {view === 'form' && renderForm()}
            {view === 'confirm' && renderConfirm()}
            {view === 'result' && renderResult()}
          </>
        )}
      </Box>
      {footer}
    </Drawer>
  );
};
